import React, { useState, useContext, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import { DirectionContext } from "../App";

const PlaintCheckPage = () => {
  const isDesktop = useMediaQuery({ minWidth: 768 });
  const { navigateTo } = useContext(DirectionContext);
  const [plaintData, setPlaintData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPlaint, setShowPlaint] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [activeTab, setActiveTab] = useState("위자료");
  const [currentSlide, setCurrentSlide] = useState(1);

  useEffect(() => {
    // 세션 스토리지에서 소장 데이터 가져오기
    const storedData = sessionStorage.getItem("plaintData");
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        setPlaintData(parsedData);
      } catch (error) {
        console.error("데이터 파싱 오류:", error);
        navigateTo("/plaint");
      }
    } else {
      // 데이터가 없으면 소장 조회 페이지로 이동
      navigateTo("/plaint");
    }
    setLoading(false);
  }, [navigateTo]);

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 성공 모달 표시 함수
  const handleShowSuccessModal = () => {
    setShowSuccessModal(true);
  };

  // 슬라이드 이미지 경로 가져오기
  const getSlideImages = () => {
    if (activeTab === "위자료") {
      return ["/png/nomalplaint.png", "/png/nomalplaint2.png"];
    } else {
      return ["/png/specialplaint.png", "/png/specialplaint2.png"];
    }
  };

  // 다음 슬라이드로 이동
  const nextSlide = () => {
    const images = getSlideImages();
    if (currentSlide < images.length) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  // 이전 슬라이드로 이동
  const prevSlide = () => {
    if (currentSlide > 1) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  // 소장 가져오기 함수
  const handleDownloadPlaint = async () => {
    setShowDownloadModal(true);
    setIsDownloading(true);
    setDownloadProgress(0);
    setDownloadStatus("소장 가져오는 중...");

    try {
      // 4초 안에 완료되는 랜덤 프로그레스바
      const startTime = Date.now();
      const duration = 2500; // 4초
      const statusMessages = ["소장 가져오는 중..."];

      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min((elapsed / duration) * 100, 100);

        // 랜덤한 증가량으로 자연스럽게 조정
        const randomAdjustment = (Math.random() - 0.5) * 5; // -2.5 ~ +2.5
        const adjustedProgress = Math.max(
          0,
          Math.min(100, progress + randomAdjustment)
        );

        setDownloadProgress(Math.floor(adjustedProgress));
        setDownloadStatus(
          adjustedProgress >= 100 ? "소장 가져오기 완료!" : statusMessages[0]
        );

        if (elapsed < duration) {
          // 랜덤한 딜레이 (50-150ms)
          const delay = Math.floor(Math.random() * 100) + 50;
          setTimeout(updateProgress, delay);
        }
      };

      updateProgress();

      // 4초 후 완료 보장
      await new Promise((resolve) => setTimeout(resolve, duration));

      // 완료 후 잠시 대기 후 모달 닫고 소장 표시
      setTimeout(() => {
        setShowDownloadModal(false);
        setIsDownloading(false);
        setDownloadProgress(0);
        setDownloadStatus("");
        setShowPlaint(true);
      }, 1000);
    } catch (error) {
      setDownloadStatus("오류가 발생했습니다.");
      setTimeout(() => {
        setShowDownloadModal(false);
        setIsDownloading(false);
        setDownloadProgress(0);
        setDownloadStatus("");
      }, 2000);
    }
  };

  if (loading) {
    return (
      <PageBackground>
        <Container>
          <LoadingText>소장을 불러오는 중...</LoadingText>
        </Container>
      </PageBackground>
    );
  }

  if (!plaintData) {
    return (
      <PageBackground>
        <Container>
          <ErrorText>소장 데이터를 찾을 수 없습니다.</ErrorText>
        </Container>
      </PageBackground>
    );
  }

  const application = plaintData.applications?.[0];

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          <div>
            <Title>{showPlaint ? "소장 확인" : "접수 현황"}</Title>
          </div>

          {!showPlaint && (
            <Header>
              <SubTitle>소장 접수 현황</SubTitle>
              <SubTitle2>접수된 소장 정보를 확인해 주세요.</SubTitle2>
            </Header>
          )}

          {!showPlaint ? (
            <>
              <PlaintContainer>
                <Section>
                  <StatusBadge>소장접수 완료</StatusBadge>
                  <InfoGrid>
                    <InfoItem>
                      <InfoLabel>신청인명</InfoLabel>
                      <InfoValue>{plaintData.name}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>휴대폰 번호</InfoLabel>
                      <InfoValue>{plaintData.phoneNumber}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>생년월일</InfoLabel>
                      <InfoValue>{plaintData.birthDate}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                      <InfoLabel>내용</InfoLabel>
                      <InfoValue>SK텔레콤 유심 해킹 손해배상 청구</InfoValue>
                    </InfoItem>
                  </InfoGrid>

                  <DateSection>
                    <DateInfo>
                      <DateLabel>접수일자</DateLabel>
                      <DateValue>
                        {new Date(plaintData.createdAt)
                          .toLocaleDateString("ko-KR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                          .replace(/\./g, "-")
                          .replace(/\s/g, "")
                          .replace(/-$/, "")}
                      </DateValue>
                    </DateInfo>
                  </DateSection>
                </Section>
              </PlaintContainer>

              <ButtonsContainer>
                <ActionButton
                  onClick={() => handleDownloadPlaint()}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  소장 확인하기
                </ActionButton>
              </ButtonsContainer>
            </>
          ) : (
            <PlaintViewContainer>
              {/* 탭 컨테이너 */}
              <TabSection>
                <TabContainer>
                  <TabButton
                    active={activeTab === "위자료"}
                    position="first"
                    onClick={() => {
                      setActiveTab("위자료");
                      setCurrentSlide(1);
                    }}
                  >
                    위자료 소장
                  </TabButton>
                  <TabButton
                    active={activeTab === "적극손해"}
                    position="last"
                    onClick={() => {
                      setActiveTab("적극손해");
                      setCurrentSlide(1);
                    }}
                  >
                    적극적 손해 소장
                  </TabButton>
                </TabContainer>
              </TabSection>

              {/* 소송 유형 안내 섹션 */}
              <GuideSection>
                <GuideTitle>소송 유형 안내</GuideTitle>
                <GuideItems>
                  {activeTab === "위자료" && (
                    <GuideItem>
                      <GuideItemIcon>
                        <GuideIcon src="/png/paintIcon.png" alt="위자료" />
                      </GuideItemIcon>
                      <GuideItemContent>
                        <GuideItemTitle>위자료 손해배상 청구</GuideItemTitle>
                        <GuideItemDescription>
                          단순 위자료 청구만 하신 분들이 해당됩니다.
                        </GuideItemDescription>
                      </GuideItemContent>
                    </GuideItem>
                  )}
                  {activeTab === "적극손해" && (
                    <GuideItem>
                      <GuideItemIcon>
                        <GuideIcon src="/png/paintIcon2.png" alt="적극손해" />
                      </GuideItemIcon>
                      <GuideItemContent>
                        <GuideItemTitle>적극적 손해배상 청구</GuideItemTitle>
                        <GuideItemDescription>
                          증거자료를 제출하신 분들이 해당됩니다.
                        </GuideItemDescription>
                      </GuideItemContent>
                    </GuideItem>
                  )}
                </GuideItems>
              </GuideSection>

              {/* 이미지 슬라이드 컨테이너 */}
              <SlideContainer>
                <SlideImage
                  src={getSlideImages()[currentSlide - 1]}
                  alt={`${activeTab} 소장 ${currentSlide}페이지`}
                />

                {/* 슬라이드 네비게이션 */}
                <SlideNavigation>
                  {currentSlide > 1 && (
                    <NavButton onClick={prevSlide} position="left">
                      <ArrowIcon2
                        src="/png/arrow.png"
                        alt="다음"
                        rotate="right"
                      />
                    </NavButton>
                  )}
                  {currentSlide < getSlideImages().length && (
                    <NavButton onClick={nextSlide} position="right">
                      <ArrowIcon
                        src="/png/arrow.png"
                        alt="이전"
                        rotate="left"
                      />
                    </NavButton>
                  )}
                </SlideNavigation>
              </SlideContainer>

              {/* 조회완료 버튼 */}
              <ButtonsContainer>
                <ActionButton
                  onClick={() => navigateTo("/")}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  조회 완료
                </ActionButton>
              </ButtonsContainer>
            </PlaintViewContainer>
          )}

          {/* 다운로드 진행 모달 */}
          <AnimatePresence>
            {showDownloadModal && (
              <ModalOverlay>
                <ModalContainer
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                  {/* <ProgressDots>
                    <Dot />
                    <Dot />
                    <Dot />
                  </ProgressDots> */}
                  <ModalContent
                    style={{
                      textAlign: "center",
                      padding: "20px 0",
                      fontWeight: "bold",
                      fontSize: "16px",
                    }}
                  >
                    {downloadStatus} {downloadProgress}%
                  </ModalContent>
                </ModalContainer>
              </ModalOverlay>
            )}
          </AnimatePresence>

          {/* 성공 모달 */}
          <AnimatePresence>
            {showSuccessModal && (
              <ModalOverlay>
                <ModalContainer
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                  <ModalTitle>소장 가져오기 완료</ModalTitle>
                  <ModalContent>
                    소장을 성공적으로 가져왔습니다.
                    <br />
                    이제 소장 내용을 확인할 수 있습니다.
                  </ModalContent>
                  <ModalButtonGroup>
                    <ModalConfirmButton
                      onClick={() => {
                        setShowSuccessModal(false);
                        setShowPlaint(true);
                      }}
                      style={{ width: "100%" }}
                    >
                      확인
                    </ModalConfirmButton>
                  </ModalButtonGroup>
                </ModalContainer>
              </ModalOverlay>
            )}
          </AnimatePresence>
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f8f8f8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #fff;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f8f8f8;

  /* 모바일 디바이스 */
  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  /* 데스크탑 디바이스 */
  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 600px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 30px 20px 40px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-top: 20px;
  margin-bottom: 30px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Title = styled.h1`
  color: #00285b;
  text-align: center;
  font-family: Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  letter-spacing: -0.34px;
  margin: 5px 0;
  ${"" /* padding-top: 10px; */}
  word-break: keep-all;
`;

const SubTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 10px;
  word-break: keep-all;
  color: #2e3840;
  text-align: left;
  width: 100%;
`;

const SubTitle2 = styled.h2`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.3;
  margin-bottom: 20px;
  word-break: keep-all;
  color: #4b5563;
  text-align: left;
  width: 100%;
`;

const PlaintContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 30px;
  margin-bottom: 40px;
`;

const Section = styled.div`
  width: 100%;
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e5e5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #2e3840;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #0075ff;
`;

const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
`;

const InfoLabel = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: #666;
`;

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  text-align: right;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 6px 9px;
  border-radius: 4px;
  border: 1px solid #0075ff;
  background: #fff;
  color: #0075ff;
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  margin-bottom: 20px;
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 40px;
  padding: 0 0px;

  @media (min-width: 769px) {
    padding: 0;
    background-color: transparent;
  }
`;

const ActionButton = styled(motion.button)`
  display: flex;
  width: 100%;
  max-width: 400px;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;

  @media (min-width: 769px) {
    max-width: 430px;
    margin: 0 auto;
  }
`;

const LoadingText = styled.div`
  font-size: 18px;
  color: #666;
  text-align: center;
  padding: 40px;
`;

const ErrorText = styled.div`
  font-size: 18px;
  color: #ff4c4c;
  text-align: center;
  padding: 40px;
`;

// 소장 가져오기 관련 스타일
const DownloadDescription = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 20px;
  text-align: center;
  line-height: 1.5;
`;

const DownloadButton = styled(motion.button)`
  display: flex;
  width: 100%;
  max-width: 300px;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  border-radius: 8px;
  background: #0075ff;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 117, 255, 0.3);

  &:hover {
    background: #0056cc;
  }
`;

const DownloadProgressContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #e5e5e5;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #0075ff, #00a3ff);
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${(props) => props.progress}%;
`;

const ProgressText = styled.div`
  font-size: 14px;
  color: #666;
  text-align: center;
  font-weight: 500;
`;

const ProgressPercentage = styled.div`
  font-size: 18px;
  color: #0075ff;
  font-weight: 600;
`;

// 모달 관련 스타일
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  pointer-events: auto;
  touch-action: none;
`;

const ModalContainer = styled(motion.div)`
  width: 70%;
  max-width: 400px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  padding: 25px 24px 25px 24px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  min-height: 100px;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 18px 0;
  color: #333;
`;

const ModalContent = styled.div`
  padding: 0 0 28px 0;
  font-size: 16px;
  color: #333;
  text-align: left;
  line-height: 1.6;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  padding: 0;
  margin-top: 8px;
`;

const ModalConfirmButton = styled.button`
  height: 44px;
  width: 100%;
  background-color: #0075ff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
`;

// 소장 이미지 관련 스타일
const PlaintImage = styled.img`
  width: 100%;
  height: auto;
  ${"" /* margin: 20px; */}
  margin-top: 20px;
  ${"" /* border-radius: 8px; */}
  object-fit: contain;
  max-width: 100%;
  padding: 0 0px;
  box-sizing: border-box;
`;

// 새로운 스타일 컴포넌트들
const GuideSection = styled.div`
  margin: 16px auto;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e5e5e5;
  background: #fff;
  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.05);
  max-width: 600px;
  width: 100%;
`;

const GuideTitle = styled.h3`
  color: #000;
  font-family: "Pretendard Variable";
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  margin: 0 0 20px 0;
`;

const GuideItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const GuideItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const GuideItemIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${(props) => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const GuideIcon = styled.img`
  width: 40px;
  height: 40px;
  ${"" /* filter: brightness(0) invert(1); */}
`;

const GuideItemContent = styled.div`
  flex: 1;
`;

const GuideItemTitle = styled.h4`
  color: #000;
  font-family: "Pretendard Variable";
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  margin: 0 0 4px 0;
`;

const GuideItemDescription = styled.p`
  color: #666;
  font-family: "Pretendard Variable";
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin: 0;
`;

const TabSection = styled.div`
  display: flex;
  ${"" /* padding: 20px; */}
  background-color: #f8f8f8;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
  justify-content: center;
  margin-top: 20px;
`;

const TabContainer = styled.div`
  display: flex;
  background-color: #d9d9d9;
  border-radius: 12px;
  padding: 4px 8px;
  gap: 0;
  width: 100%;
  max-width: 600px;
  height: 56px;
  align-items: center;
`;

const SlideContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${"" /* padding: 20px; */}
  background-color: white;
  position: relative;
  overflow-y: auto;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
`;

const SlideImage = styled.img`
  width: 100%;
  max-width: 600px;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  object-fit: contain;
`;

const SlideNavigation = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  transform: translateY(-50%);
  pointer-events: none;
`;

const NavButton = styled.button`
  position: absolute;
  top: 0;
  ${(props) => props.position}: 20px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.2);
  color: white;
  border: none;
  font-size: 18px;
  cursor: pointer;
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7);
  }
`;

const ArrowIcon = styled.img`
  width: 20px;
  height: 20px;
  filter: brightness(0) invert(1);
  transform: ${(props) =>
    props.rotate === "left"
      ? "rotate(90deg)"
      : props.rotate === "right"
      ? "rotate(-90deg)"
      : "rotate(0deg)"};
  display: block;
  margin-left: ${(props) => (props.position === "left" ? "0px" : "4px")};
  margin-right: ${(props) => (props.position === "right" ? "-5px" : "0px")};
`;
const ArrowIcon2 = styled.img`
  width: 20px;
  height: 20px;
  filter: brightness(0) invert(1);
  transform: ${(props) =>
    props.rotate === "left"
      ? "rotate(90deg)"
      : props.rotate === "right"
      ? "rotate(-90deg)"
      : "rotate(0deg)"};
  display: block;
  margin-left: ${(props) => (props.position === "left" ? "0px" : "-4px")};
  margin-right: ${(props) => (props.position === "right" ? "-5px" : "0px")};
`;

// 날짜 섹션 스타일
const DateSection = styled.div`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e5e5;
`;

const DateInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DateValue = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const DateLabel = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

// 진행 표시 점들 스타일
const ProgressDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 5px;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  background-color: #666;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.4;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.2);
    }
  }

  &:nth-child(2) {
    animation-delay: 0.2s;
  }

  &:nth-child(3) {
    animation-delay: 0.4s;
  }
`;

// 소장 보기 컨테이너 스타일
const PlaintViewContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: #f8f8f8;
  display: flex;
  flex-direction: column;
`;

const TabButton = styled.button`
  padding: ${(props) => {
    if (props.active) {
      return props.position === "first"
        ? "10px 16px 10px 20px"
        : props.position === "last"
        ? "10px 20px 10px 16px"
        : "10px 16px";
    }
    return "12px 20px";
  }};
  border: none;
  background-color: ${(props) => (props.active ? "#F2F2F2" : "transparent")};
  color: ${(props) => (props.active ? "#0D002F" : "#595E65")};
  border-radius: ${(props) => (props.active ? "8px" : "0")};

  text-align: center;
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: ${(props) => (props.active ? 700 : 600)};
  line-height: normal;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  position: relative;
  flex: 1;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:first-child {
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }

  &:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }

  &:hover {
    background-color: ${(props) =>
      props.active ? "#E8E8E8" : "rgba(255, 255, 255, 0.1)"};
  }
`;

export default PlaintCheckPage;
